import React from 'react';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import { jobApplicationStore } from 'ats/pages/pipeline/components/JobApplicationStore';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { eventManager } from 'ats/utils/EventManager';

export const FINAL_APPLICANT_STAGE_TO_JOB_APPLICATION_STAGE = {
	discarded_people_skills: 'rejected_by_recruiter',
	discarded_cultural_unfit: 'rejected_by_recruiter',
	discarded_technical_skills: 'rejected_by_recruiter',
	candidate_out_of_reach: 'unresponsive',
	rejected_the_process: 'not_interested'
};

const NON_CASCADABLE_JOB_APPLICATION_STATUSES = [
	'rejected',
	'withdrawn',
	'hired'
];

export const isFinalApplicantStage = stage =>
	Object.prototype.hasOwnProperty.call(
		FINAL_APPLICANT_STAGE_TO_JOB_APPLICATION_STAGE,
		stage
	);

export const filterCascadableJobApplications = jobApplications =>
	(jobApplications || []).filter(jobApplication => {
		const isOpenJob = jobApplication?.job?.status === 'open';
		const currentStatus =
			jobApplicationStore.stageToStatus[jobApplication?.stage];
		const isInProgress =
			!NON_CASCADABLE_JOB_APPLICATION_STATUSES.includes(currentStatus);
		return isOpenJob && isInProgress;
	});

const cascadeJobApplicationStage = async (
	jobApplications,
	targetJobApplicationStage,
	onSuccess
) => {
	const ids = jobApplications.map(jobApplication => jobApplication.id);
	try {
		const result = await jobApplicationStore.bulkUpdate(ids, {
			stage: targetJobApplicationStage
		});

		if (result.status === 'success') {
			notificationStore.pushSuccessNotification(
				'Job Applications Updated',
				`Successfully updated ${ids.length} job application(s).`
			);

			jobApplications.forEach(jobApplication => {
				eventManager.emit('jobApplicationStageChanged', {
					jobApplicationId: jobApplication.id,
					stage: targetJobApplicationStage,
					applicantId: jobApplication.applicantId,
					jobId: jobApplication.job?.id
				});
			});

			onSuccess?.();
		} else {
			notificationStore.pushErrorNotification(
				'Update Failed',
				'Failed to update some job applications.'
			);
		}
	} catch (error) {
		notificationStore.pushErrorNotification(
			'Update Failed',
			'Failed to update job applications.'
		);
	}
};

const fetchJobApplicationsForApplicants = async applicantIds => {
	const results = await Promise.all(
		applicantIds.map(applicantId =>
			applicantStore.get(applicantId).catch(() => null)
		)
	);

	return results.flatMap(result => {
		if (!result || result.status !== 'success') return [];
		return result.data?.jobApplications || [];
	});
};

/**
 * If `newStage` is a final applicant stage, fetch the affected applicants'
 * job applications, then prompt the user to also move the open-job
 * applications to the corresponding terminal job-application stage.
 *
 * Accepts either `applicantIds` (will fetch fresh data) or
 * `preloadedJobApplications` (when caller already has them with `job.status`).
 */
export const offerJobApplicationCascade = async ({
	newStage,
	applicantIds,
	preloadedJobApplications,
	onSuccess
}) => {
	const targetJobApplicationStage =
		FINAL_APPLICANT_STAGE_TO_JOB_APPLICATION_STAGE[newStage];
	if (!targetJobApplicationStage) return;

	const ids = Array.isArray(applicantIds)
		? applicantIds
		: applicantIds != null
			? [applicantIds]
			: [];

	const jobApplications =
		preloadedJobApplications ?? (await fetchJobApplicationsForApplicants(ids));

	const cascadable = filterCascadableJobApplications(jobApplications);
	if (cascadable.length === 0) return;

	const targetJobApplicationStageData = jobApplicationStore.stageOptions.find(
		stage => stage.value === targetJobApplicationStage
	);
	const applicantStageLabel = applicantStore.stageOptions.find(
		stage => stage.value === newStage
	)?.label;
	const count = cascadable.length;
	const applicantCount = ids.length || 1;

	dialogStore.openDialog(
		{
			title: 'Update Open Job Applications?',
			message: (
				<>
					{applicantCount === 1
						? 'This applicant was'
						: `${applicantCount} applicants were`}{' '}
					moved to <strong>{applicantStageLabel}</strong>. There{' '}
					{count === 1 ? 'is' : 'are'} <strong>{count}</strong> active job
					application{count === 1 ? '' : 's'} on open jobs. Would you also like
					to move {count === 1 ? 'it' : 'them'} to{' '}
					<strong>{targetJobApplicationStageData?.label}</strong>?
				</>
			),
			confirmLabel: 'Yes, Update Applications',
			cancelLabel: 'No, Skip',
			confirmButtonClass: 'is-primary'
		},
		() =>
			cascadeJobApplicationStage(
				cascadable,
				targetJobApplicationStage,
				onSuccess
			)
	);
};
